import { earliestScheduleValue, toLocalValue } from './pushHistoryView';

// Riyadh calendar maths without a date library. A day is "YYYY-MM-DD" and a
// value "YYYY-MM-DDTHH:mm", both Riyadh wall-clock; the arithmetic runs on
// UTC timestamps so the Mac's own time zone never moves a day boundary.

export const HORIZON_DAYS = 60;
// 8 pm: the evening, when customers are on their phones.
export const DEFAULT_TIME = '20:00';

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const utc = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};
const isoDay = (ms) => new Date(ms).toISOString().slice(0, 10);
const pad = (n) => String(n).padStart(2, '0');

export const riyadhToday = (now = Date.now()) => toLocalValue(new Date(now)).slice(0, 10);
export const addDays = (date, days) => isoDay(utc(date) + days * DAY);
/** 0 = Sunday … 6 = Saturday. */
export const weekday = (date) => new Date(utc(date)).getUTCDay();

export const splitValue = (value) => {
  const [date = '', time = ''] = (value || '').split('T');
  return { date, time };
};
export const joinValue = (date, time) => (date && time ? `${date}T${time}` : '');

/** "8:00 PM" for "20:00". */
export const timeLabel = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${h % 12 || 12}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
};

/** Every half hour of the day, as the time list offers them. */
export const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const value = `${pad(Math.floor(i / 2))}:${i % 2 ? '30' : '00'}`;
  return { value, label: timeLabel(value) };
});

/** "Fri 6 Nov 2026". */
export const dateLabel = (date) => {
  if (!date) return '';
  const [y, m, d] = date.split('-').map(Number);
  return `${WEEKDAYS[weekday(date)]} ${d} ${MONTHS[m - 1].slice(0, 3)} ${y}`;
};

/** "November 2026" for any day of the month. */
export const monthLabel = (date) => {
  const [y, m] = date.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

/** The first day of the month ``delta`` months from ``date``'s. */
export const shiftMonth = (date, delta) => {
  let [y, m] = date.split('-').map(Number);
  m += delta;
  while (m > 12) { m -= 12; y += 1; }
  while (m < 1) { m += 12; y -= 1; }
  return `${y}-${pad(m)}-01`;
};

/**
 * The weeks of ``date``'s month, Sunday first, each a row of seven days; the
 * neighbouring months' days fill the first and last rows.
 */
export const monthGrid = (date) => {
  const month = date.slice(0, 7);
  const first = `${month}-01`;
  let cursor = addDays(first, -weekday(first));
  const weeks = [];
  do {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({ date: cursor, inMonth: cursor.slice(0, 7) === month });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  } while (cursor.slice(0, 7) === month);
  return weeks;
};

/** Whether a day can be picked: today, up to HORIZON_DAYS ahead. */
export const dayAllowed = (date, now = Date.now()) => {
  const today = riyadhToday(now);
  return date >= today && date <= addDays(today, HORIZON_DAYS);
};

/** Whether ``time`` on ``date`` can be picked: a few minutes ahead at least. */
export const slotAllowed = (date, time, now = Date.now()) => joinValue(date, time) >= earliestScheduleValue(now);

/** The first time of ``date`` that can still be picked, preferring ``wanted``; '' when the day is over. */
export const firstAllowedSlot = (date, wanted, now = Date.now()) => {
  if (wanted && slotAllowed(date, wanted, now)) return wanted;
  const slot = TIME_SLOTS.find((s) => slotAllowed(date, s.value, now));
  return slot ? slot.value : '';
};

/**
 * The usual choices, each at 8 pm Riyadh: tonight (tomorrow once 8 pm is
 * here), then the coming Friday and Saturday, when customers shop on their
 * phones. A day that is also tonight is listed once.
 */
export const quickPicks = (now = Date.now()) => {
  const today = riyadhToday(now);
  const evening = (date) => joinValue(date, DEFAULT_TIME);
  const next = (day) => {
    let date = today;
    while (weekday(date) !== day || !slotAllowed(date, DEFAULT_TIME, now)) date = addDays(date, 1);
    return date;
  };
  const first = slotAllowed(today, DEFAULT_TIME, now)
    ? { key: 'tonight', label: 'Tonight', value: evening(today) }
    : { key: 'tomorrow', label: 'Tomorrow', value: evening(addDays(today, 1)) };
  const picks = [first, { key: 'friday', label: 'Friday', value: evening(next(5)) }, { key: 'saturday', label: 'Saturday', value: evening(next(6)) }];
  const seen = new Set();
  return picks
    .filter((pick) => !seen.has(pick.value) && seen.add(pick.value))
    .map((pick) => ({ ...pick, label: `${pick.label} ${timeLabel(DEFAULT_TIME)}` }));
};

/** "in 25 min" / "in 9 hours" / "in 2 days" until a value goes out. */
export const relativeLabel = (value, now = Date.now()) => {
  const delta = Date.parse(`${value}:00+03:00`) - now;
  if (!(delta >= MINUTE)) return 'now';
  const minutes = Math.round(delta / MINUTE);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(delta / (60 * MINUTE));
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(delta / DAY);
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

// The time picker's clock face: hours 1–12 with AM/PM, minutes 0–59.

/** ``n`` kept inside [min, max], wrapping round at either end. */
export const wrap = (n, min, max) => {
  const span = max - min + 1;
  return min + ((((n - min) % span) + span) % span);
};

/** "20:17" as the picker shows it: { hour: 8, minute: 17, period: 'PM' }. */
export const to12h = (time) => {
  const [h, m] = (time || DEFAULT_TIME).split(':').map(Number);
  return { hour: h % 12 || 12, minute: m, period: h < 12 ? 'AM' : 'PM' };
};

/** The picker's clock face as a "HH:mm" value: 8, 17, 'PM' → "20:17". */
export const to24h = (hour, minute, period) => `${pad((hour % 12) + (period === 'PM' ? 12 : 0))}:${pad(minute)}`;
